import React from 'react';
import { Col, Row } from 'reactstrap';
import { Formik, Form, Field } from 'formik';
import { FormPhotoUpload, NeoCard, NeoButton, NeoInput } from '../../../../components/Common';
import { tenantSchema } from '../../../../models/tenant/tenantSchema';
import { tenantDefaults } from '../../../../models/tenant/tenantDefaults';

// ── Helper: seção dentro do card ──
const SectionHeader = ({ icon, title, subtitle }) => (
    <div className="d-flex align-items-center gap-3 mb-4">
        <div className="neo-section-icon-circle flex-shrink-0">
            <i className={`mdi ${icon} font-size-18 text-accent`} />
        </div>
        <div>
            <h6 className="mb-0 fw-bold text-dark neo-font-outfit font-size-15">{title}</h6>
            {subtitle && <p className="text-muted mb-0 font-size-12">{subtitle}</p>}
        </div>
    </div>
);

// ── Helper: divisor de seção ──
const SectionDivider = ({ icon, title, subtitle }) => (
    <div className="border-top pt-4 mt-2">
        <SectionHeader icon={icon} title={title} subtitle={subtitle} />
    </div>
);

const CompanyForm = ({ initialData, onSave, isSaving }) => {
    const formValues = { ...tenantDefaults, ...initialData };

    return (
        <Formik
            enableReinitialize
            initialValues={formValues}
            validationSchema={tenantSchema}
            onSubmit={(values, { setSubmitting }) => {
                onSave(values);
                setSubmitting(false);
            }}
        >
            {({ isSubmitting, setFieldValue, values }) => (
                <Form>
                    <NeoCard bodyClassName="p-4">

                        {/* ── SEÇÃO 1: INFORMAÇÕES DA EMPRESA ── */}
                        <SectionHeader
                            icon="mdi-office-building"
                            title="Informações da Empresa"
                            subtitle="Dados cadastrais básicos"
                        />

                        <Row className="mb-4">
                            <Col md={3} className="text-center mb-4 mb-md-0 d-flex flex-column align-items-center justify-content-center border-end pe-md-4">
                                <FormPhotoUpload
                                    photoPreview={values.logoUrl}
                                    onChange={(base64Str) => setFieldValue('logoUrl', base64Str)}
                                    size="lg"
                                    objectFit="contain"
                                />
                                <span className="text-muted font-size-11 fw-bold text-uppercase mt-2 letter-spacing-1">
                                    Logo da Empresa
                                </span>
                            </Col>
                            <Col md={9} className="ps-md-4">
                                <Row>
                                    <Col md={6}>
                                        <Field name="tradeName">
                                            {({ field, meta }) => (
                                                <NeoInput
                                                    {...field}
                                                    label="Nome Fantasia *"
                                                    placeholder="Ex: Academia Fit"
                                                    error={meta.touched && meta.error ? meta.error : ''}
                                                    className="mb-3"
                                                />
                                            )}
                                        </Field>
                                    </Col>
                                    <Col md={6}>
                                        <Field name="corporateName">
                                            {({ field, meta }) => (
                                                <NeoInput
                                                    {...field}
                                                    label="Razão Social"
                                                    placeholder="Razão Social LTDA"
                                                    error={meta.touched && meta.error ? meta.error : ''}
                                                    className="mb-3"
                                                />
                                            )}
                                        </Field>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={4}>
                                        <Field name="document">
                                            {({ field, meta }) => (
                                                <NeoInput
                                                    {...field}
                                                    label="CNPJ"
                                                    placeholder="00.000.000/0000-00"
                                                    error={meta.touched && meta.error ? meta.error : ''}
                                                    className="mb-3"
                                                />
                                            )}
                                        </Field>
                                    </Col>
                                    <Col md={4}>
                                        <Field name="email">
                                            {({ field, meta }) => (
                                                <NeoInput
                                                    {...field}
                                                    type="email"
                                                    label="E-mail Comercial"
                                                    placeholder="contato@empresa.com"
                                                    error={meta.touched && meta.error ? meta.error : ''}
                                                    className="mb-3"
                                                />
                                            )}
                                        </Field>
                                    </Col>
                                    <Col md={4}>
                                        <Field name="phone">
                                            {({ field, meta }) => (
                                                <NeoInput
                                                    {...field}
                                                    label="Telefone / WhatsApp"
                                                    placeholder="(11) 90000-0000"
                                                    error={meta.touched && meta.error ? meta.error : ''}
                                                    className="mb-3"
                                                />
                                            )}
                                        </Field>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>

                        {/* ── SEÇÃO 2: MODALIDADES ── */}
                        <SectionDivider
                            icon="mdi-run-fast"
                            title="Ramo Principal / Modalidades"
                            subtitle="Selecione as modalidades que sua empresa oferece"
                        />

                        <Row className="mb-4">
                            {[
                                { value: "musculacao", label: "Musculação", icon: "mdi mdi-dumbbell", color: "#2e7d32" },
                                { value: "crossfit", label: "CrossFit / Funcional", icon: "mdi mdi-lightning-bolt", color: "#e2661c" },
                                { value: "natacao", label: "Natação", icon: "mdi mdi-swim", color: "#2196f3" },
                                { value: "corrida", label: "Corrida / Assessoria", icon: "mdi mdi-run-fast", color: "#784b22" },
                            ].map((branch) => {
                                const currentBranches = values.primaryBranch || [];
                                const isChecked = currentBranches.includes(branch.value);
                                return (
                                    <Col md={3} sm={6} key={branch.value} className="mb-2">
                                        <div
                                            className={`neo-branch-option d-flex align-items-center gap-3 p-3 cursor-pointer ${isChecked ? 'neo-branch-option--active' : ''}`}
                                            onClick={() => {
                                                const next = isChecked
                                                    ? currentBranches.filter(b => b !== branch.value)
                                                    : [...currentBranches, branch.value];
                                                setFieldValue('primaryBranch', next);
                                            }}
                                        >
                                            <div className="neo-branch-icon flex-shrink-0" style={{ color: isChecked ? branch.color : undefined }}>
                                                <i className={branch.icon} />
                                            </div>
                                            <span className={`fw-bold font-size-13 neo-font-outfit flex-grow-1 ${isChecked ? 'text-dark' : 'text-muted'}`}>
                                                {branch.label}
                                            </span>
                                            <input
                                                type="checkbox"
                                                className="form-check-input mb-0"
                                                checked={isChecked}
                                                onChange={() => { }}
                                                readOnly
                                            />
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>

                        {/* ── SEÇÃO 3: ENDEREÇO ── */}
                        <SectionDivider
                            icon="mdi-map-marker-outline"
                            title="Localização / Endereço"
                            subtitle="Endereço comercial da empresa"
                        />

                        <Row className="mb-4">
                            <Col md={3}>
                                <Field name="zipCode">
                                    {({ field }) => (
                                        <NeoInput {...field} label="CEP" placeholder="00000-000" className="mb-3" />
                                    )}
                                </Field>
                            </Col>
                            <Col md={7}>
                                <Field name="address">
                                    {({ field }) => (
                                        <NeoInput {...field} label="Logradouro" placeholder="Rua / Avenida" className="mb-3" />
                                    )}
                                </Field>
                            </Col>
                            <Col md={2}>
                                <Field name="number">
                                    {({ field }) => (
                                        <NeoInput {...field} label="Número" placeholder="123" className="mb-3" />
                                    )}
                                </Field>
                            </Col>
                        </Row>
                        <Row className="mb-4">
                            <Col md={4}>
                                <Field name="complement">
                                    {({ field }) => (
                                        <NeoInput {...field} label="Complemento" placeholder="Apto, Sala..." className="mb-3" />
                                    )}
                                </Field>
                            </Col>
                            <Col md={4}>
                                <Field name="neighborhood">
                                    {({ field }) => (
                                        <NeoInput {...field} label="Bairro" className="mb-3" />
                                    )}
                                </Field>
                            </Col>
                            <Col md={3}>
                                <Field name="city">
                                    {({ field }) => (
                                        <NeoInput {...field} label="Cidade" className="mb-3" />
                                    )}
                                </Field>
                            </Col>
                            <Col md={1}>
                                <Field name="state">
                                    {({ field }) => (
                                        <NeoInput {...field} label="UF" placeholder="SP" className="mb-3" />
                                    )}
                                </Field>
                            </Col>
                        </Row>

                        {/* ── SEÇÃO 4: COORDENADOR TÉCNICO ── */}
                        <SectionDivider
                            icon="mdi-account-tie-outline"
                            title="Coordenador Técnico"
                            subtitle="Responsável técnico pelas atividades físicas"
                        />

                        <Row className="mb-4">
                            <Col md={3} className="text-center mb-4 mb-md-0 d-flex flex-column align-items-center justify-content-center border-end pe-md-4">
                                <FormPhotoUpload
                                    photoPreview={values.technicalCoordinator?.photoUrl}
                                    onChange={(base64Str) => setFieldValue('technicalCoordinator.photoUrl', base64Str)}
                                    size="lg"
                                />
                                <span className="text-muted font-size-11 fw-bold text-uppercase mt-2 letter-spacing-1">
                                    Foto do Coordenador
                                </span>
                            </Col>
                            <Col md={9} className="ps-md-4">
                                <Row>
                                    <Col md={6}>
                                        <Field name="technicalCoordinator.firstName">
                                            {({ field, meta }) => (
                                                <NeoInput
                                                    {...field}
                                                    label="Nome *"
                                                    placeholder="Nome"
                                                    error={meta.touched && meta.error ? meta.error : ''}
                                                    className="mb-3"
                                                />
                                            )}
                                        </Field>
                                    </Col>
                                    <Col md={6}>
                                        <Field name="technicalCoordinator.lastName">
                                            {({ field, meta }) => (
                                                <NeoInput
                                                    {...field}
                                                    label="Sobrenome"
                                                    placeholder="Sobrenome"
                                                    error={meta.touched && meta.error ? meta.error : ''}
                                                    className="mb-3"
                                                />
                                            )}
                                        </Field>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Field name="technicalCoordinator.email">
                                            {({ field, meta }) => (
                                                <NeoInput
                                                    {...field}
                                                    type="email"
                                                    label="E-mail"
                                                    placeholder="coordenador@empresa.com"
                                                    error={meta.touched && meta.error ? meta.error : ''}
                                                    className="mb-3"
                                                />
                                            )}
                                        </Field>
                                    </Col>
                                    <Col md={6}>
                                        <Field name="technicalCoordinator.phone">
                                            {({ field, meta }) => (
                                                <NeoInput
                                                    {...field}
                                                    label="Telefone"
                                                    placeholder="(11) 90000-0000"
                                                    error={meta.touched && meta.error ? meta.error : ''}
                                                    className="mb-3"
                                                />
                                            )}
                                        </Field>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>

                        {/* ── SEÇÃO 5: REPRESENTANTE LEGAL ── */}
                        <SectionDivider
                            icon="mdi-file-sign"
                            title="Representante Legal"
                            subtitle="Signatário do contrato de prestação de serviços"
                        />

                        <Row className="mb-4">
                            <Col md={6}>
                                <Field name="legalRepresentative.name">
                                    {({ field }) => (
                                        <NeoInput {...field} label="Nome do Representante" placeholder="Nome Completo" className="mb-3" />
                                    )}
                                </Field>
                            </Col>
                            <Col md={6}>
                                <Field name="legalRepresentative.cpf">
                                    {({ field }) => (
                                        <NeoInput {...field} label="CPF" placeholder="000.000.000-00" className="mb-3" />
                                    )}
                                </Field>
                            </Col>
                        </Row>
                        <Row className="mb-2">
                            <Col md={6}>
                                <Field name="legalRepresentative.email">
                                    {({ field }) => (
                                        <NeoInput {...field} type="email" label="E-mail" placeholder="rep@empresa.com" className="mb-3" />
                                    )}
                                </Field>
                            </Col>
                            <Col md={6}>
                                <Field name="legalRepresentative.phone">
                                    {({ field }) => (
                                        <NeoInput {...field} label="Telefone" placeholder="(11) 90000-0000" className="mb-3" />
                                    )}
                                </Field>
                            </Col>
                        </Row>

                        {/* ── BOTÃO SALVAR ── */}
                        <div className="d-flex justify-content-end border-top pt-4 mt-2">
                            <NeoButton
                                type="submit"
                                variant="primary"
                                loading={isSaving || isSubmitting}
                                loadingText="Salvando..."
                                icon="mdi-content-save-outline"
                                disabled={isSaving || isSubmitting}
                            >
                                Salvar Alterações
                            </NeoButton>
                        </div>

                    </NeoCard>
                </Form>
            )}
        </Formik>
    );
};

export default CompanyForm;
